import { auth } from './firebase';

export class ApiHttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(await authHeader())) headers.set(k, v);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed: ${res.status}`;
    throw new ApiHttpError(res.status, message);
  }
  return body as T;
}

export type TicketSortField =
  | 'id'
  | 'title'
  | 'status'
  | 'priority'
  | 'area'
  | 'isCustomerService'
  | 'rank'
  | 'updatedAt'
  | 'createdAt'
  | 'assignee'
  // Priority high→low, then least recently updated first.
  | 'priorityStale';

export interface TicketListResponse {
  tickets: TicketSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const Tickets = {
  list: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') q.set(k, String(v));
    }
    return api<TicketListResponse>(
      `/api/tickets${q.size ? `?${q.toString()}` : ''}`,
    );
  },
  get: (id: number) => api<{ ticket: TicketFull }>(`/api/tickets/${id}`),
  create: (body: CreateTicketBody) =>
    api<{ ticket: TicketSummary }>(`/api/tickets`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patch: (id: number, body: PatchTicketBody) =>
    api<{ ticket: TicketSummary }>(`/api/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: number) => api<null>(`/api/tickets/${id}`, { method: 'DELETE' }),
  claim: (id: number) =>
    api<{ ticket: TicketFull }>(`/api/tickets/${id}/claim`, { method: 'POST' }),
  ping: (id: number, body: { userIds: string[]; message?: string }) =>
    api<{ comment: TicketComment }>(`/api/tickets/${id}/ping`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  comment: (
    id: number,
    body: string,
    mentionUserIds: string[] = [],
    attachmentIds: string[] = [],
  ) =>
    api<{ comment: TicketComment }>(`/api/tickets/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body, mentionUserIds, attachmentIds }),
    }),
  setNotifyLists: (id: number, listIds: string[]) =>
    api<{ ticket: TicketSummary }>(`/api/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ notifyListIds: listIds }),
    }),
  addLink: (id: number, linkedTicketId: number, linkType: TicketLinkType) =>
    api<{ link: TicketLinkRecord }>(`/api/tickets/${id}/links`, {
      method: 'POST',
      body: JSON.stringify({ linkedTicketId, linkType }),
    }),
  removeLink: (id: number, linkId: string) =>
    api<null>(`/api/tickets/${id}/links/${linkId}`, { method: 'DELETE' }),
  confirmAttachment: (id: number, body: ConfirmAttachmentBody) =>
    api<{ attachment: AttachmentRecord }>(`/api/tickets/${id}/attachments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeAttachment: (id: number, attachmentId: string) =>
    api<null>(`/api/tickets/${id}/attachments/${attachmentId}`, { method: 'DELETE' }),
  reorder: (ticketIds: number[]) =>
    api<null>(`/api/tickets/reorder`, {
      method: 'POST',
      body: JSON.stringify({ ticketIds }),
    }),
  reopen: (id: number, reason: string) =>
    api<{ ticket: TicketFull }>(`/api/tickets/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  humanizeResolution: (id: number) =>
    api<{ ticket: TicketFull }>(`/api/tickets/${id}/resolution/humanize`, {
      method: 'POST',
    }),
  clearAiNotes: (id: number) =>
    api<{ ticket: TicketFull }>(`/api/tickets/${id}/ai-notes`, { method: 'DELETE' }),
};

export type DeploymentDisposition =
  | 'PENDING_REPORTER_REVIEW'
  | 'PENDING_CUSTOMER_REVIEW'
  | 'RESOLVED';

/**
 * Where a deploy ticket's linked ORIGINAL goes when the deploy ticket is
 * resolved in the batch. 'leave' means don't touch the original.
 */
export type PostDeployOriginalStatus =
  | 'PENDING_REPORTER_REVIEW'
  | 'PENDING_CUSTOMER_REVIEW'
  | 'RESOLVED'
  | 'leave';

export type DeploymentKind =
  | 'BUGFIX'
  | 'FEATURE'
  | 'INFRA'
  | 'CONFIG'
  | 'DEPENDENCY'
  | 'HOTFIX'
  | 'ROLLBACK'
  | 'OTHER';

export interface DeploymentItemInput {
  ticketId: number;
  status: DeploymentDisposition;
  /** Only meaningful for a deploy ticket resolved in the batch (ticket #338). */
  postDeployOriginalStatus?: PostDeployOriginalStatus;
}

export interface DeploymentRow {
  id: string;
  kind: DeploymentKind;
  title: string | null;
  refUrl: string | null;
  notes: string | null;
  createdAt: string;
  deployedBy: { id: string; name: string; email: string };
  tickets: { ticket: { id: number; title: string; status: TicketStatus } }[];
}

export const Deployments = {
  list: (limit = 25) =>
    api<{ deployments: DeploymentRow[] }>(`/api/deployments?limit=${limit}`),
  create: (body: {
    kind?: DeploymentKind;
    title?: string;
    refUrl?: string;
    notes?: string;
    items?: DeploymentItemInput[];
  }) =>
    api<{ deployment: DeploymentRow }>(`/api/deployments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const AffectedAreas = {
  list: (includeInactive = false) =>
    api<{ affectedAreas: AffectedAreaRow[] }>(
      `/api/affected-areas${includeInactive ? '?includeInactive=true' : ''}`,
    ),
  create: (body: { name: string; sortOrder?: number; isActive?: boolean }) =>
    api<{ affectedArea: AffectedAreaRow }>(`/api/affected-areas`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: number, body: { name?: string; sortOrder?: number; isActive?: boolean }) =>
    api<{ affectedArea: AffectedAreaRow }>(`/api/affected-areas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<null | { affectedArea: AffectedAreaRow; deactivated: true; usageCount: number; message: string }>(
      `/api/affected-areas/${id}`,
      { method: 'DELETE' },
    ),
};

export const Users = {
  list: () => api<{ users: UserSummary[] }>(`/api/users`),
};

/**
 * Where a ticket sits in the j1l-projects hierarchy. `feature` and `task` are
 * independently nullable: a link can be project-only, project+feature, or the
 * full chain.
 */
export interface TicketProjectLink {
  id: string;
  project: { id: number; name: string };
  feature: { id: string; title: string } | null;
  task: { id: string; title: string } | null;
}

export const Projects = {
  /**
   * Reverse lookup into the projects app. Served by the j1l-projects Fastify
   * plugin, which registers this route on the ticketing `/api/*` namespace —
   * so it needs no separate origin, rewrite, or proxy entry.
   *
   * Returns `{ links: [] }` (not a 403) for users without projects access, and
   * 404s entirely when the plugin isn't mounted. Callers must treat every
   * failure as "nothing to show" — `api()` throws on any non-2xx.
   */
  linksForTicket: (ticketId: number) =>
    api<{ links: TicketProjectLink[] }>(`/api/tickets/${ticketId}/project-links`),
};

export const Me = {
  get: () => api<{ user: UserSummary }>(`/api/me`),
};

export type ActivityEntry =
  | {
      kind: 'ticket_event';
      id: string;
      createdAt: string;
      eventType: string;
      fromValue: unknown;
      toValue: unknown;
      actor: UserSummary;
      ticket: { id: number; title: string };
    }
  | {
      kind: 'suggestion_created';
      id: string;
      createdAt: string;
      actor: UserSummary;
      suggestion: { id: number; title: string };
    };

export const Activity = {
  list: (params: { limit?: number; before?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.before) q.set('before', params.before);
    return api<{ events: ActivityEntry[]; nextCursor: string | null }>(
      `/api/activity${q.size ? `?${q.toString()}` : ''}`,
    );
  },
};

export const DistributionLists = {
  list: () => api<{ lists: DistributionList[] }>(`/api/distribution-lists`),
  get: (id: string) => api<{ list: DistributionList }>(`/api/distribution-lists/${id}`),
  create: (body: { name: string; description?: string; isDefault?: boolean; members: DistMemberInput[] }) =>
    api<{ list: DistributionList }>(`/api/distribution-lists`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (
    id: string,
    body: {
      name?: string;
      description?: string | null;
      isDefault?: boolean;
      members?: DistMemberInput[];
    },
  ) =>
    api<{ list: DistributionList }>(`/api/distribution-lists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    api<null>(`/api/distribution-lists/${id}`, { method: 'DELETE' }),
};

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  hasSignedIn: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AreaDeployConfigRow {
  area: TicketArea;
  reviewerUserId: string | null;
  updatedAt: string;
  reviewer: UserSummary | null;
}

export const Admin = {
  listUsers: () => api<{ users: AdminUserRow[] }>(`/api/admin/users`),
  updateUser: (id: string, body: { role?: UserRole; active?: boolean }) =>
    api<{ user: AdminUserRow }>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getAreaDeployConfig: () =>
    api<{ config: AreaDeployConfigRow[] }>(`/api/admin/area-deploy-config`),
  setAreaDeployReviewer: (area: TicketArea, reviewerUserId: string | null) =>
    api<{ config: AreaDeployConfigRow }>(`/api/admin/area-deploy-config/${area}`, {
      method: 'PUT',
      body: JSON.stringify({ reviewerUserId }),
    }),
};

export interface DeviceTokenRow {
  id: string;
  description: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export const DeviceTokens = {
  list: () => api<{ tokens: DeviceTokenRow[] }>(`/api/me/device-tokens`),
  create: (description?: string) =>
    api<{ token: string; record: { id: string; description: string | null; createdAt: string } }>(
      `/api/me/device-tokens`,
      { method: 'POST', body: JSON.stringify({ description }) },
    ),
  revoke: (id: string) =>
    api<null>(`/api/me/device-tokens/${id}`, { method: 'DELETE' }),
};

export type SuggestionStatus = 'OPEN' | 'PLANNED' | 'DONE' | 'DECLINED';

export interface SuggestionRow {
  id: number;
  authorUserId: string;
  title: string;
  body: string;
  status: SuggestionStatus;
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
  author: { id: string; name: string; email: string };
}

export const Suggestions = {
  list: (status?: SuggestionStatus) => {
    const q = status ? `?status=${status}` : '';
    return api<{ suggestions: SuggestionRow[]; total: number; page: number; limit: number; totalPages: number }>(
      `/api/suggestions${q}`,
    );
  },
  get: (id: number) =>
    api<{ suggestion: SuggestionRow }>(`/api/suggestions/${id}`),
  create: (body: { title: string; body: string }) =>
    api<{ suggestion: SuggestionRow }>(`/api/suggestions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (
    id: number,
    body: { title?: string; body?: string; status?: SuggestionStatus; adminNote?: string | null },
  ) =>
    api<{ suggestion: SuggestionRow }>(`/api/suggestions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<null>(`/api/suggestions/${id}`, { method: 'DELETE' }),
};

export interface ReportResponse {
  range: { days: number; startUtc: string; endUtc: string };
  createdVsResolved: { date: string; created: number; resolved: number }[];
  openByStatusOverTime: {
    date: string;
    OPEN: number;
    IN_PROGRESS: number;
    BLOCKED: number;
    PENDING_CODE_REVIEW: number;
    READY_TO_DEPLOY: number;
    PENDING_CUSTOMER_REVIEW: number;
    PENDING_REPORTER_REVIEW: number;
    PENDING_VERIFICATION: number;
  }[];
  resolutionTimes: {
    overall: { count: number; medianMs: number | null; p90Ms: number | null };
    byPriority: { key: TicketPriority; count: number; medianMs: number | null; p90Ms: number | null }[];
    byAffectedArea: { key: string; count: number; medianMs: number | null; p90Ms: number | null }[];
  };
  currentBreakdowns: {
    area: { key: TicketArea; count: number }[];
    priority: { key: TicketPriority; count: number }[];
    cause: { key: TicketCause; count: number }[];
    affectedArea: { key: string; count: number }[];
    totalActive: number;
  };
  /** Tickets whose resolvedAt falls within the requested range, newest first. */
  resolvedTickets: ResolvedTicketRow[];
}

export interface ResolvedTicketRow {
  id: number;
  title: string;
  priority: TicketPriority;
  createdAt: string;
  resolvedAt: string;
  /** Milliseconds from createdAt to resolvedAt. */
  resolutionMs: number;
  assigneeName: string | null;
  /** User who flipped status → RESOLVED on the most recent such transition. */
  resolverName: string | null;
  affectedAreas: string[];
}

export interface DailyAssigneeRow {
  assigneeUserId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  resolvedCount: number;
  avgResolutionMs: number;
}

export interface DailyReportResponse {
  /** YYYY-MM-DD (UTC day). */
  date: string;
  startUtc: string;
  endUtc: string;
  createdCount: number;
  resolvedCount: number;
  byAssignee: DailyAssigneeRow[];
}

export const Reports = {
  get: (days: number) => api<ReportResponse>(`/api/reports?days=${days}`),
  daily: (date?: string) =>
    api<DailyReportResponse>(`/api/reports/daily${date ? `?date=${date}` : ''}`),
};

// ─── Types ────────────────────────────────────────────────────────────────

export type UserRole = 'ADMIN' | 'DEVELOPER' | 'STAFF';

export type TicketStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'BLOCKED'
  | 'PENDING_CODE_REVIEW'
  | 'READY_TO_DEPLOY'
  | 'PENDING_CUSTOMER_REVIEW'
  | 'PENDING_REPORTER_REVIEW'
  | 'PENDING_VERIFICATION'
  | 'FUTURE'
  | 'RESOLVED';
export type TicketView = 'all' | 'active';
export type TicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type TicketArea = 'CUSTOMER_WEBSITE' | 'PRODUCTION' | 'OTHER';
export type IntakeChannel = 'EMAIL' | 'PHONE' | 'IN_PERSON' | 'OTHER';
export type TicketCause = 'LEGACY' | 'PREVIOUS_FIX' | 'PERSON' | 'CUSTOMER' | 'NEW_PROBLEM';
export type TicketLinkType = 'CAUSED_BY' | 'RELATED_TO' | 'DUPLICATE_OF' | 'DEPLOY_OF';

export interface AffectedAreaRow {
  id: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TicketAffectedAreaLink {
  id: string;
  ticketId: number;
  affectedAreaId: number;
  affectedArea: { id: number; name: string };
}

export interface UserSummary {
  id: string;
  email: string;
  name: string;
  role?: UserRole;
}

export interface TicketRef {
  id: number;
  title: string;
  status: TicketStatus;
}

export interface TicketSummary {
  id: number;
  title: string;
  description: string;
  stepsToReproduce: string | null;
  resolution: string | null;
  humanResolution: string | null;
  /** Estimated hours. Prisma Decimal — serialized as a string, e.g. "2.5". */
  estimatedHours: string | null;
  blockedReason: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  area: TicketArea;
  intakeChannel: IntakeChannel;
  isCustomerService: boolean;
  /** True for auto-generated "Deploy: #N — …" tickets that track a PR + deploy. */
  isDeployTicket: boolean;
  /** For a deploy ticket, the original it ships (DEPLOY_OF). Only set by the list endpoint. */
  deployOfTicketId?: number | null;
  affectedAreas: TicketAffectedAreaLink[];
  estimatedCompletion: string | null; // YYYY-MM-DD — developer's fix ETA
  dueDate: string | null; // YYYY-MM-DD — the customer order's due date
  rush: boolean;
  customerCompany: string | null;
  customerContact: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  cause: TicketCause;
  causeUserId: string | null;
  causeTicketId: number | null;
  reporterUserId: string;
  assigneeUserId: string | null;
  rank: number | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  createdBy: UserSummary;
  reporter: UserSummary;
  assignee: UserSummary | null;
  causeUser: UserSummary | null;
  causeTicket: TicketRef | null;
  notifyLists?: TicketNotifyListRef[];
  aiNotes: string | null;
  aiNotesModel: string | null;
  aiNotesUpdatedAt: string | null;
  aiNotesAuthorUserId: string | null;
  aiNotesAuthor: UserSummary | null;
}

export interface TicketComment {
  id: string;
  ticketId: number;
  body: string;
  createdAt: string;
  author: UserSummary;
  mentions: CommentMention[];
  /** Files attached specifically to this comment (e.g. pasted screenshots). */
  attachments?: AttachmentRecord[];
}

export interface CommentMention {
  id: string;
  commentId: string;
  userId: string;
  user: UserSummary;
}

export interface TicketEvent {
  id: string;
  eventType: string;
  fromValue: unknown;
  toValue: unknown;
  createdAt: string;
  actor: UserSummary;
}

export interface TicketLinkRecord {
  id: string;
  ticketId: number;
  linkedTicketId: number;
  linkType: TicketLinkType;
  createdAt: string;
  createdBy: UserSummary;
  linkedTicket: TicketRef;
}

export interface IncomingTicketLink {
  id: string;
  linkType: TicketLinkType;
  createdAt: string;
  createdBy: UserSummary;
  ticket: TicketRef; // the ticket that points to us
}

export interface AttachmentRecord {
  id: string;
  ticketId: number;
  commentId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  createdAt: string;
  uploader: UserSummary;
}

export interface TicketFull extends TicketSummary {
  causedBy: TicketRef[];
  comments: TicketComment[];
  events: TicketEvent[];
  outgoingLinks: TicketLinkRecord[];
  incomingLinks: IncomingTicketLink[];
  attachments: AttachmentRecord[];
  notifyLists: TicketNotifyListRef[];
  notifyEmails: TicketNotifyEmailRef[];
}

export interface TicketNotifyListRef {
  id: string;
  ticketId: number;
  listId: string;
  list: { id: string; name: string };
}

export interface TicketNotifyEmailRef {
  id: string;
  ticketId: number;
  email: string;
  createdAt: string;
}

export interface DistributionList {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: UserSummary;
  members: DistributionListMember[];
}

export interface DistributionListMember {
  id: string;
  listId: string;
  userId: string | null;
  email: string | null;
  user: UserSummary | null;
}

export interface DistMemberInput {
  userId?: string | null;
  email?: string | null;
}

export interface CreateTicketBody {
  title: string;
  description: string;
  stepsToReproduce?: string;
  resolution?: string;
  area: TicketArea;
  priority: TicketPriority;
  intakeChannel: IntakeChannel;
  isCustomerService?: boolean;
  affectedAreaIds?: number[];
  estimatedCompletion?: string | null;
  dueDate?: string | null;
  rush?: boolean;
  reporterUserId?: string;
  assigneeUserId?: string | null;
  customerCompany?: string;
  customerContact?: string;
  customerEmail?: string;
  customerPhone?: string;
  cause: TicketCause;
  causeUserId?: string | null;
  causeTicketId?: number | null;
  notifyListIds?: string[];
  notifyEmails?: string[];
}

export interface PatchTicketBody {
  title?: string;
  description?: string;
  stepsToReproduce?: string | null;
  resolution?: string | null;
  humanResolution?: string | null;
  estimatedHours?: number | null;
  blockedReason?: string | null;
  status?: TicketStatus;
  priority?: TicketPriority;
  area?: TicketArea;
  intakeChannel?: IntakeChannel;
  isCustomerService?: boolean;
  affectedAreaIds?: number[];
  estimatedCompletion?: string | null;
  dueDate?: string | null;
  rush?: boolean;
  reporterUserId?: string;
  assigneeUserId?: string | null;
  customerCompany?: string | null;
  customerContact?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  cause?: TicketCause;
  causeUserId?: string | null;
  causeTicketId?: number | null;
  notifyListIds?: string[];
  notifyEmails?: string[];
  rank?: number | null;
  /** Only meaningful when transitioning a deploy ticket to RESOLVED. */
  postDeployOriginalStatus?:
    | 'PENDING_CUSTOMER_REVIEW'
    | 'PENDING_REPORTER_REVIEW'
    | 'RESOLVED'
    | 'leave';
}

export interface ConfirmAttachmentBody {
  storagePath: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  commentId?: string;
}
